import { HighlightItem } from './TextHighLightingService';
import { getCachedObjects } from "@/lib/stores/objectsStore";
import { getCachedTypes } from '@/lib/stores/typesStore';
import { getSelectedGame } from '@/lib/stores/unifiedUserStore';

export interface HighlightDataRegistration {
  items: HighlightItem[];
  source: string; // e.g., 'glossary', 'types', 'tags'
  priority?: number; // Higher priority items override lower priority ones
}

export class HighlightDataService {
  private static registeredData: Map<string, HighlightDataRegistration> = new Map();

  /**
   * Get real glossary items from the store for the current game
   * @returns Array of highlight items from glossary items
   */
  private static getGlossaryHighlightItems(): HighlightItem[] {
    const selectedGame = getSelectedGame();
    if (!selectedGame?.id) {
      return [];
    }

    const glossaryItems = getCachedObjects(selectedGame.id);
    const highlightItems: HighlightItem[] = [];

    glossaryItems.forEach(item => {
      const keys: string[] = [];
      
      // Add the main name
      if (item.name) {
        keys.push(item.name);
      }
      
      // Add all identifiers
      if (item.identifiers && item.identifiers.length > 0) {
        keys.push(...item.identifiers);
      }
      
      // Create a single highlight item with all keys pointing to the same glossary item ID
      if (keys.length > 0) {
        highlightItems.push({
          keys: keys,
          type: 'glossary',
          id: item.id,
          name: item.name, // Store the item name for display
          metadata: {
            gameId: selectedGame.id,
            itemType: 'glossary'
          }
        });
      }
    });

    return highlightItems;
  }

  /**
   * Get variable values from the types store for the current game
   * @returns Array of highlight items from variable values
   */
  private static getTypesHighlightItems(): HighlightItem[] {
    const selectedGame = getSelectedGame();
    if (!selectedGame?.id) {
      return [];
    }

    const types = getCachedTypes(selectedGame.id);
    const highlightItems: HighlightItem[] = [];

    types.forEach(type => {
      // Add the type name itself as a highlightable item
      if (type.name) {
        highlightItems.push({
          keys: [type.name],
          type: 'type',
          id: type.id,
          name: type.name,
          metadata: {
            gameId: selectedGame.id,
            itemType: 'type',
            typeId: type.id
          }
        });
      }
      
      // Also add each variable value within the type (like "light", "heavy", etc.)
      if (type.data && type.data.length > 0) {
        type.data.forEach(element => {
          if (element.name) {
            highlightItems.push({
              keys: [element.name],
              type: 'type-value',
              id: `${type.id}-${element.id}`,
              name: `${type.name}: ${element.name}`, // Display as "Type: Value"
              metadata: {
                gameId: selectedGame.id,
                itemType: 'type-value',
                typeName: type.name,
                typeId: type.id,
                elementId: element.id
              }
            });
          }
        });
      }
    });

    return highlightItems;
  }

  /**
   * Register highlight data from a store or component
   * @param registration - The highlight data registration
   */
  static registerHighlightData(registration: HighlightDataRegistration): void {
    this.registeredData.set(registration.source, registration);
  }

  /**
   * Unregister highlight data from a specific source
   * @param source - The source identifier
   */
  static unregisterHighlightData(source: string): void {
    this.registeredData.delete(source);
  }

  /**
   * Get all registered highlight items, sorted by priority
   * @returns Array of highlight items
   */
  static getRegisteredHighlightItems(): HighlightItem[] {
    const allItems: HighlightItem[] = [];
    
    // Add real glossary data first (lowest priority)
    allItems.push(...this.getGlossaryHighlightItems());
    
    // Add type values (medium priority)
    allItems.push(...this.getTypesHighlightItems());
    
    // Add registered data, sorted by priority
    const registrations = Array.from(this.registeredData.values())
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
    
    registrations.forEach(registration => {
      allItems.push(...registration.items);
    });
    
    return allItems;
  }

  /**
   * Get highlight items from a specific source
   * @param source - The source identifier
   * @returns Array of highlight items from that source
   */
  static getHighlightItemsBySource(source: string): HighlightItem[] {
    if (source === 'glossary') {
      return this.getGlossaryHighlightItems();
    }
    
    if (source === 'types') {
      return this.getTypesHighlightItems();
    }
    
    const registration = this.registeredData.get(source);
    return registration ? registration.items : [];
  }

  /**
   * Get all registered sources
   * @returns Array of source identifiers
   */
  static getRegisteredSources(): string[] {
    const sources = Array.from(this.registeredData.keys());
    // Always include glossary and types as sources
    if (!sources.includes('glossary')) {
      sources.unshift('glossary');
    }
    if (!sources.includes('types')) {
      sources.splice(1, 0, 'types');
    }
    return sources;
  }

  /**
   * Clear all registered data (useful for testing or resetting)
   */
  static clearAllRegisteredData(): void {
    this.registeredData.clear();
  }

  /**
   * Get statistics about registered data
   * @returns Object with statistics
   */
  static getStatistics(): {
    totalItems: number;
    registeredSources: number;
    glossaryItems: number;
    typeItems: number;
    sourceBreakdown: Record<string, number>;
  } {
    const registeredItems = Array.from(this.registeredData.values())
      .flatMap(reg => reg.items);
    const glossaryItems = this.getGlossaryHighlightItems();
    const typeItems = this.getTypesHighlightItems();
    
    const sourceBreakdown: Record<string, number> = {
      glossary: glossaryItems.length,
      types: typeItems.length
    };
    
    this.registeredData.forEach((reg, source) => {
      sourceBreakdown[source] = reg.items.length;
    });
    
    return {
      totalItems: glossaryItems.length + typeItems.length + registeredItems.length,
      registeredSources: this.registeredData.size + 2, // +2 for glossary and types
      glossaryItems: glossaryItems.length,
      typeItems: typeItems.length,
      sourceBreakdown
    };
  }

  /**
   * Search highlight items by key or type
   * @param query - Search query
   * @returns Array of matching highlight items
   */
  static searchHighlightItems(query: string): HighlightItem[] {
    const allItems = this.getRegisteredHighlightItems();
    const lowerQuery = query.toLowerCase();
    
    return allItems.filter(item => 
      item.keys.some(key => key.toLowerCase().includes(lowerQuery)) ||
      item.type.toLowerCase().includes(lowerQuery) ||
      item.id.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get highlight items by type
   * @param type - The type to filter by
   * @returns Array of highlight items of that type
   */
  static getHighlightItemsByType(type: string): HighlightItem[] {
    const allItems = this.getRegisteredHighlightItems();
    return allItems.filter(item => item.type === type);
  }

  /**
   * Test method to verify highlighting works with multi-word type values
   * @param testText - Text to test highlighting on
   * @returns Highlighted text result
   */
  static testHighlighting(testText: string = "This item is not so heavy and very light."): any {
    const highlightItems = this.getRegisteredHighlightItems();
    
    // Import the text highlighting service dynamically to avoid circular dependencies
    import('./TextHighLightingService').then(({ textHighlightingService }) => {
      // Clear any existing highlights
      textHighlightingService.clearAllHighlights();
      
      // Add our highlight items
      textHighlightingService.addHighlightItems(highlightItems);
      
      // Process the test text
      const result = textHighlightingService.processText(testText);
      
      
      return result;
    }).catch(error => {
      console.error("Error testing highlighting:", error);
    });
  }
}

// Export a singleton instance if needed
export const highlightDataService = HighlightDataService;
